import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import {
  startBlog,
  reviewBlog,
  type BlogPayload,
  type KeywordRow,
} from "../../api/blogApi";

// ─── Constants ────────────────────────────────────────────────────────────────

const PURPLE = "#7c4dff";
const PURPLE_LIGHT = "#ede7ff";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step =
  | "idle"
  | "loading"
  | "research"
  | "titles"
  | "draft"
  | "published"
  | "error";

interface BlogState {
  step: Step;
  loadingMsg: string;
  threadId: string;
  error: string;
  // Research
  researchSummary: string;
  suggestedTopics: string[];
  keywordTable: KeywordRow[];
  // Titles
  titleOptions: string[];
  // Draft
  selectedTitle: string;
  blogDraft: string;
  // Published
  publishResult: string;
  liveUrl: string;
}

const INIT: BlogState = {
  step: "idle",
  loadingMsg: "",
  threadId: "",
  error: "",
  researchSummary: "",
  suggestedTopics: [],
  keywordTable: [],
  titleOptions: [],
  selectedTitle: "",
  blogDraft: "",
  publishResult: "",
  liveUrl: "",
};

// ─── Step progress labels ─────────────────────────────────────────────────────

const STEPS = ["Research", "Topics", "Titles", "Draft", "Published"];

const stepIndex: Record<Step, number> = {
  idle: -1,
  loading: -1,
  error: -1,
  research: 1,
  titles: 2,
  draft: 3,
  published: 4,
};

// ─── Component ────────────────────────────────────────────────────────────────

export const BlogStudioPage: React.FC = () => {
  const [state, setState] = useState<BlogState>(INIT);
  const [topicInput, setTopicInput] = useState("");
  const [rejectionFeedback, setRejectionFeedback] = useState("");
  const [showFeedbackBox, setShowFeedbackBox] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [draftFeedback, setDraftFeedback] = useState("");
  const [showDraftFeedback, setShowDraftFeedback] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const patch = (partial: Partial<BlogState>) =>
    setState((prev) => ({ ...prev, ...partial }));

  const setLoading = (msg: string) =>
    patch({ step: "loading", loadingMsg: msg });

  const handlePayload = (data: BlogPayload) => {
    if (data.status === "published") {
      const lines = String(data.publish_result || "").split("\n");
      const slugLine = lines.find((l) => l.startsWith("published:")) ?? "";
      const slug = slugLine.replace("published:", "").trim();
      patch({
        step: "published",
        threadId: data.thread_id,
        selectedTitle: data.selected_title,
        publishResult: data.publish_result,
        liveUrl: slug
          ? `https://blogging.sds-1vz.pages.dev/${slug}/`
          : "",
      });
      return;
    }

    const { stage, thread_id } = data as { stage: string; thread_id: string };

    if (stage === "research_approval_node") {
      const d = data as Extract<BlogPayload, { stage: "research_approval_node" }>;
      patch({
        step: "research",
        threadId: thread_id,
        researchSummary: d.research_summary ?? "",
        suggestedTopics: d.suggested_topics ?? [],
        keywordTable: d.keyword_table ?? [],
      });
    } else if (stage === "title_approval_node") {
      const d = data as Extract<BlogPayload, { stage: "title_approval_node" }>;
      patch({ step: "titles", threadId: thread_id, titleOptions: d.title_options ?? [] });
    } else if (stage === "human_approval_node") {
      const d = data as Extract<BlogPayload, { stage: "human_approval_node" }>;
      patch({
        step: "draft",
        threadId: thread_id,
        selectedTitle: d.selected_title ?? "",
        blogDraft: d.blog_draft ?? "",
      });
    }
  };

  const resume = async (body: {
    action: string;
    selection?: string;
    feedback?: string;
    loadingMsg?: string;
  }) => {
    const { loadingMsg = "Processing...", ...rest } = body;
    setLoading(loadingMsg);
    try {
      const data = await reviewBlog({ thread_id: state.threadId, ...rest });
      handlePayload(data);
    } catch (e: unknown) {
      patch({ step: "error", error: String((e as Error)?.message ?? e) });
    }
  };

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleStart = async () => {
    setLoading("Running Tavily web research & generating topic recommendations…");
    try {
      const data = await startBlog(topicInput.trim() || undefined);
      handlePayload(data);
    } catch (e: unknown) {
      patch({ step: "error", error: String((e as Error)?.message ?? e) });
    }
  };

  const handleTopicSelect = (idx: number) =>
    resume({ action: "select", selection: String(idx + 1), loadingMsg: "Topic selected — generating titles…" });

  const handleTopicReject = () => {
    if (!rejectionFeedback.trim()) {
      setShowFeedbackBox(true);
      return;
    }
    resume({
      action: "reject",
      feedback: rejectionFeedback.trim(),
      loadingMsg: "Running new research for different topics…",
    });
    setRejectionFeedback("");
    setShowFeedbackBox(false);
  };

  const handleTitleSelect = (idx: number) =>
    resume({ action: "select", selection: String(idx + 1), loadingMsg: "Writing your blog post…" });

  const handleTitleCustom = () => {
    if (!customTitle.trim()) return;
    resume({ action: "custom", feedback: customTitle.trim(), loadingMsg: "Writing your blog post…" });
    setCustomTitle("");
  };

  const handleDraftApprove = () =>
    resume({ action: "approve", loadingMsg: "Building site and deploying to Cloudflare Pages…" });

  const handleDraftReject = () => {
    if (!draftFeedback.trim()) {
      setShowDraftFeedback(true);
      return;
    }
    resume({ action: "reject", feedback: draftFeedback.trim(), loadingMsg: "Revising blog draft…" });
    setDraftFeedback("");
    setShowDraftFeedback(false);
  };

  const handleReset = () => {
    setState(INIT);
    setTopicInput("");
    setRejectionFeedback("");
    setShowFeedbackBox(false);
    setCustomTitle("");
    setDraftFeedback("");
    setShowDraftFeedback(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const curStep = stepIndex[state.step] ?? -1;

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f7fb",
        padding: "24px 16px",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .topic-card:hover { border-color: ${PURPLE} !important; background-color: ${PURPLE_LIGHT} !important; }
        .title-card:hover { border-color: ${PURPLE} !important; background-color: #f8f4ff !important; }
        .blog-content h1, .blog-content h2, .blog-content h3 { color: #333; margin-top: 24px; }
        .blog-content p { color: #555; line-height: 1.7; margin-bottom: 16px; }
        .blog-content ul, .blog-content ol { color: #555; padding-left: 20px; }
        .blog-content li { margin-bottom: 6px; line-height: 1.6; }
        .blog-content code { background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
        .blog-content blockquote { border-left: 3px solid ${PURPLE}; padding-left: 16px; color: #777; margin: 16px 0; }
        .blog-content table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
        .blog-content th, .blog-content td { border: 1px solid #e0e0e0; padding: 8px 12px; text-align: left; }
        .blog-content th { background: #f5f7fb; font-weight: 600; }
      `}</style>

      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: "bold", color: "#333", margin: 0 }}>
              Blog Studio
            </h1>
            <p style={{ color: "#888", margin: "4px 0 0 0", fontSize: "13px" }}>
              AI-powered blog generation for SDS — research, write, and deploy in one flow
            </p>
          </div>
          <Link
            to="/"
            style={{ color: PURPLE, textDecoration: "none", fontSize: "14px", whiteSpace: "nowrap" }}
          >
            ← Back to Chat
          </Link>
        </div>

        {/* Step progress bar */}
        {state.step !== "idle" && state.step !== "error" && (
          <div
            style={{
              display: "flex",
              gap: "6px",
              marginBottom: "24px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div
                  style={{
                    padding: "4px 14px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: i <= curStep ? "bold" : "normal",
                    backgroundColor:
                      i === curStep
                        ? PURPLE
                        : i < curStep
                        ? "#e8f5e9"
                        : "#e8e8e8",
                    color:
                      i === curStep ? "#fff" : i < curStep ? "#2e7d32" : "#aaa",
                    transition: "all 0.2s",
                  }}
                >
                  {i < curStep ? "✓ " : ""}
                  {s}
                </div>
                {i < STEPS.length - 1 && (
                  <span style={{ color: "#ccc", fontSize: "12px" }}>›</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── IDLE ──────────────────────────────────────────────────────── */}
        {state.step === "idle" && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "48px 40px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "52px", marginBottom: "16px" }}>✍️</div>
            <h2 style={{ fontSize: "22px", fontWeight: "bold", color: "#333", margin: "0 0 8px 0" }}>
              SDS Blog Agent
            </h2>
            <p
              style={{
                color: "#777",
                marginBottom: "32px",
                maxWidth: "480px",
                margin: "0 auto 32px auto",
                lineHeight: "1.6",
                fontSize: "14px",
              }}
            >
              The agent searches the web for trending AP automation topics, generates
              5 ranked topic recommendations, then writes a full SEO-optimized blog
              post and deploys it to the SDS site automatically.
            </p>
            <div style={{ maxWidth: "420px", margin: "0 auto" }}>
              <input
                type="text"
                placeholder="Optional: suggest a topic (leave blank for AI to pick)"
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleStart()}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  fontSize: "14px",
                  marginBottom: "14px",
                  boxSizing: "border-box",
                  outline: "none",
                  color: "#333",
                }}
              />
              <button
                onClick={handleStart}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "10px",
                  backgroundColor: PURPLE,
                  color: "#fff",
                  border: "none",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Start Blog Agent
              </button>
            </div>
          </div>
        )}

        {/* ── LOADING ───────────────────────────────────────────────────── */}
        {state.step === "loading" && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "72px 40px",
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                border: `4px solid ${PURPLE_LIGHT}`,
                borderTopColor: PURPLE,
                borderRadius: "50%",
                margin: "0 auto 24px auto",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ color: "#666", fontSize: "15px" }}>{state.loadingMsg}</p>
          </div>
        )}

        {/* ── RESEARCH ──────────────────────────────────────────────────── */}
        {state.step === "research" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Research summary */}
            {state.researchSummary && (
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  padding: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3 style={{ margin: "0 0 12px 0", color: "#333", fontSize: "15px" }}>
                  Research Summary
                </h3>
                <p style={{ color: "#555", lineHeight: "1.6", margin: 0, fontSize: "14px" }}>
                  {state.researchSummary}
                </p>
              </div>
            )}

            {/* Topic cards */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <h3 style={{ margin: "0 0 4px 0", color: "#333", fontSize: "15px" }}>
                Select a Topic
              </h3>
              <p style={{ color: "#999", fontSize: "13px", margin: "0 0 16px 0" }}>
                Click to use this topic for your blog post
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {state.suggestedTopics.map((topic, i) => (
                  <button
                    key={i}
                    className="topic-card"
                    onClick={() => handleTopicSelect(i)}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: `1.5px solid ${i === 0 ? PURPLE : "#e0e0e0"}`,
                      backgroundColor: i === 0 ? PURPLE_LIGHT : "#fafafa",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "14px",
                      color: "#333",
                      transition: "all 0.15s",
                    }}
                  >
                    <span
                      style={{
                        minWidth: "28px",
                        height: "28px",
                        borderRadius: "50%",
                        backgroundColor: i === 0 ? PURPLE : "#e8e8e8",
                        color: i === 0 ? "#fff" : "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {i === 0 ? "★" : i + 1}
                    </span>
                    <span style={{ fontWeight: i === 0 ? 600 : 400, lineHeight: "1.4" }}>
                      {topic}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Keyword table */}
            {state.keywordTable.length > 0 && (
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  padding: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3 style={{ margin: "0 0 16px 0", color: "#333", fontSize: "15px" }}>
                  SEO Keywords
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f5f7fb" }}>
                        {["Keyword", "Volume", "Difficulty", "Intent", "Relevance"].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "10px 12px",
                              textAlign: "left",
                              color: "#666",
                              fontWeight: 600,
                              borderBottom: "1px solid #e0e0e0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {state.keywordTable.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 12px", fontWeight: 500, color: "#333" }}>
                            {row.keyword}
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              color:
                                row.volume === "High"
                                  ? "#2e7d32"
                                  : row.volume === "Medium"
                                  ? "#e65100"
                                  : "#888",
                            }}
                          >
                            {row.volume}
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              color:
                                row.difficulty === "High"
                                  ? "#c62828"
                                  : row.difficulty === "Medium"
                                  ? "#e65100"
                                  : "#2e7d32",
                            }}
                          >
                            {row.difficulty}
                          </td>
                          <td style={{ padding: "10px 12px", color: "#555" }}>{row.intent}</td>
                          <td style={{ padding: "10px 12px", color: "#888", fontSize: "12px" }}>
                            {row.relevance}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Reject / get new topics */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "20px 24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              {showFeedbackBox ? (
                <>
                  <p style={{ color: "#555", fontSize: "14px", marginBottom: "10px", marginTop: 0 }}>
                    What direction should the agent explore?
                  </p>
                  <textarea
                    value={rejectionFeedback}
                    onChange={(e) => setRejectionFeedback(e.target.value)}
                    placeholder='e.g. "focus on duplicate payments", "try ERP integration topics", "more on three-way matching"'
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      marginBottom: "12px",
                      boxSizing: "border-box",
                      resize: "vertical",
                      outline: "none",
                    }}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={handleTopicReject}
                      disabled={!rejectionFeedback.trim()}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "none",
                        backgroundColor: rejectionFeedback.trim() ? "#e53935" : "#e0e0e0",
                        color: "#fff",
                        cursor: rejectionFeedback.trim() ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Get New Topics
                    </button>
                    <button
                      onClick={() => setShowFeedbackBox(false)}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        backgroundColor: "#fff",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setShowFeedbackBox(true)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  None of these — suggest different topics
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── TITLES ────────────────────────────────────────────────────── */}
        {state.step === "titles" && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "28px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <h3 style={{ margin: "0 0 4px 0", color: "#333", fontSize: "15px" }}>
              Pick a Title
            </h3>
            <p style={{ color: "#999", fontSize: "13px", margin: "0 0 20px 0" }}>
              Click a title or enter a custom one below
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "24px" }}>
              {state.titleOptions.map((title, i) => (
                <button
                  key={i}
                  className="title-card"
                  onClick={() => handleTitleSelect(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: "1.5px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "14px",
                    color: "#333",
                    transition: "all 0.15s",
                  }}
                >
                  <span
                    style={{
                      minWidth: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: "#e8e8e8",
                      color: "#666",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ lineHeight: "1.4" }}>{title}</span>
                </button>
              ))}
            </div>
            <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: "20px" }}>
              <p style={{ color: "#888", fontSize: "13px", marginBottom: "10px", marginTop: 0 }}>
                Or enter a custom title:
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleTitleCustom()}
                  placeholder="Your custom title…"
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleTitleCustom}
                  disabled={!customTitle.trim()}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: customTitle.trim() ? PURPLE : "#e0e0e0",
                    color: "#fff",
                    cursor: customTitle.trim() ? "pointer" : "not-allowed",
                    fontSize: "14px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Use This
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DRAFT ─────────────────────────────────────────────────────── */}
        {state.step === "draft" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "36px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <h2
                style={{
                  margin: "0 0 24px 0",
                  color: "#222",
                  fontSize: "22px",
                  lineHeight: "1.3",
                }}
              >
                {state.selectedTitle}
              </h2>
              <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: "24px" }}>
                <div className="blog-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {state.blogDraft}
                  </ReactMarkdown>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button
                  onClick={handleDraftApprove}
                  style={{
                    padding: "13px 28px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "bold",
                  }}
                >
                  Publish to SDS Site
                </button>
                <button
                  onClick={() => setShowDraftFeedback((v) => !v)}
                  style={{
                    padding: "13px 24px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  Request Revision
                </button>
              </div>

              {showDraftFeedback && (
                <div style={{ marginTop: "16px" }}>
                  <textarea
                    value={draftFeedback}
                    onChange={(e) => setDraftFeedback(e.target.value)}
                    placeholder='What should be changed? e.g. "Make section 2 more technical", "Add a comparison table"'
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "14px",
                      marginBottom: "12px",
                      boxSizing: "border-box",
                      resize: "vertical",
                      outline: "none",
                    }}
                  />
                  <button
                    onClick={handleDraftReject}
                    disabled={!draftFeedback.trim()}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: draftFeedback.trim() ? PURPLE : "#e0e0e0",
                      color: "#fff",
                      cursor: draftFeedback.trim() ? "pointer" : "not-allowed",
                      fontSize: "14px",
                    }}
                  >
                    Revise Draft
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PUBLISHED ─────────────────────────────────────────────────── */}
        {state.step === "published" && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "56px 40px",
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>🎉</div>
            <h2 style={{ color: "#2e7d32", margin: "0 0 8px 0", fontSize: "24px" }}>
              Published!
            </h2>
            <p style={{ color: "#666", marginBottom: "32px", fontSize: "15px" }}>
              {state.selectedTitle}
            </p>
            {state.liveUrl && (
              <a
                href={state.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "13px 28px",
                  backgroundColor: PURPLE,
                  color: "#fff",
                  borderRadius: "10px",
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: "15px",
                  marginBottom: "24px",
                }}
              >
                View Live Blog Post →
              </a>
            )}
            <br />
            <button
              onClick={handleReset}
              style={{
                padding: "11px 24px",
                borderRadius: "10px",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontSize: "14px",
                color: "#666",
                marginTop: "8px",
              }}
            >
              Write Another Blog
            </button>
          </div>
        )}

        {/* ── ERROR ─────────────────────────────────────────────────────── */}
        {state.step === "error" && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "48px 40px",
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h2 style={{ color: "#c62828", margin: "0 0 12px 0" }}>Something went wrong</h2>
            <p
              style={{
                color: "#666",
                marginBottom: "24px",
                fontFamily: "monospace",
                fontSize: "13px",
                backgroundColor: "#fff5f5",
                padding: "12px 16px",
                borderRadius: "8px",
                textAlign: "left",
                wordBreak: "break-word",
              }}
            >
              {state.error}
            </p>
            <button
              onClick={handleReset}
              style={{
                padding: "11px 28px",
                borderRadius: "8px",
                backgroundColor: PURPLE,
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Start Over
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
